import { payrollApi } from 'Api/payroll';
import { viewPayrollApi } from 'Api/payrollView';
import { Button, Checkbox, Col, Input, Modal, Row, Table } from 'antd';
import { useEffect, useState } from 'react';
import { toastText } from 'utils/utils';
import GlobalHeader from '../GlobalHeader';
import LeaveGratuityPayTable from './Table';
import SelectDropdown from 'components/Global/SelectDropdown';
import { SearchOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getAccessibleBranchForUser } from 'Api/masters/branch-code';
import { AppDispatch } from 'redux/store';
import { fetchConstantDropdownAction } from '../../../../redux/actions/constantDropdownAction';
import './index.scss'

type LeavePayProps = {
	payrollId: any;
	onlyView: boolean;
	headerData: any;
	setLeaveGratuityData: (data: any) => void;
	LeaveGratuityData: any;
	setIsUniversalLoading: any;
	showSteps: boolean;
};
interface DropdownOption {
	label: string;
	value: string;
}
interface EmployeeData {
	id: string;
	employeeId: string;
	employeeName: string;
	branch: string;
	department: string;
	firstName:string;
	lastName:string;
	midName:string
}
const LeavePayCalculation = (props: LeavePayProps) => {
	const [leavePayLoading, setLeavePayLoading] = useState(false);
	const [isAddEmployeeModal, setIsAddEmployeeModal] = useState(false);
	const [isEmployeeLoading, setEmployeeLoading] = useState(false);
	const [isSavingEmployees, setIsSavingEmployees] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [branchIds, setBranchIds] = useState<string[]>([]);
	const [departmentIds, setDepartmentIds] = useState<string[]>([]);
	const [accessibleBranches, setAccessibleBranches] = useState<
		DropdownOption[]
	>([]);
	const [isBranchesLoading, setIsBranchesLoading] = useState<boolean>(false);
	const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
	const [modalEmployees, setModalEmployees] = useState<EmployeeData[]>([]);
	const [modalEmployeesTotal, setModalEmployeesTotal] = useState(0);

	const tableChangeHandler = (
		pagination: any,
		filters: any,
		sorter: any
	) => {};

	const {
		payrollId,
		onlyView,
		headerData,
		setLeaveGratuityData,
		LeaveGratuityData,
		setIsUniversalLoading,
		showSteps,
	} = props;

	const dropdownOptions = useSelector(
		(state: any) => state.constantDropdown.data
	);
	const dispatch = useDispatch<AppDispatch>();

	// Fetch branches and dropdown data on component mount
	useEffect(() => {
		fetchAccessibleBranches();
		dispatch(fetchConstantDropdownAction({}));
	}, [dispatch]);

	const fetchAccessibleBranches = async () => {
		try {
			setIsBranchesLoading(true);
			const response = await getAccessibleBranchForUser();
			const branches: DropdownOption[] = response?.data?.data ?? [];
			branches.shift(); // Remove first element if needed
			setAccessibleBranches(branches);
		} catch (error: any) {
			const message =
				error?.response?.data?.message ||
				'Failed to fetch accessible branches';
			toastText(message, 'error');
		} finally {
			setIsBranchesLoading(false);
		}
	};

	// Fetch employees when modal opens or filters change
	useEffect(() => {
		if (isAddEmployeeModal) {
			fetchModalEmployees();
		}
	}, [isAddEmployeeModal, branchIds, departmentIds]);

	const fetchModalEmployees = async () => {
		try {
			setEmployeeLoading(true);

			// Build request body
			const payload = {
				payrollId: payrollId,
				branchIds: branchIds.length > 0 ? branchIds : [],
				departmentIds: departmentIds.length > 0 ? departmentIds : [],
			};

			// Call API with body
			const response =
				await payrollApi.getAdvanceEmployeeLeaveAndGratuityPay(payload);

			if (response?.data) {
				setModalEmployees(response.data.data || []);
				setModalEmployeesTotal(response.data.data?.length || 0);
			}
		} catch (error) {
			toastText('Failed to fetch employees', 'error');
			setModalEmployees([]);
		} finally {
			setEmployeeLoading(false);
		}
	};

const searchTerms = searchText.toLowerCase().trim().split(/\s+/); 

const filteredEmployees = modalEmployees.filter((emp) =>
	searchTerms.every((term) =>
		[emp.firstName, emp.lastName, emp.midName, emp.employeeId]
			.filter(Boolean)
			.some((field) => field!.toLowerCase().includes(term))
	)
);

	const handleSelectEmployee = (employeeId: string) => {
		setSelectedEmployees((prev) =>
			prev.includes(employeeId)
				? prev.filter((id) => id !== employeeId)
				: [...prev, employeeId]
		);
	};

	const handleSelectAll = () => {
		if (selectedEmployees.length === filteredEmployees.length) {
			setSelectedEmployees([]);
		} else {
			setSelectedEmployees(filteredEmployees.map((emp) => emp.id));
		}
	};

	const handleAddEmployees = async () => {
		if (selectedEmployees.length === 0) {
			toastText('Please select at least one employee', 'error');
			return;
		}

		try {
			setIsSavingEmployees(true);

			// Prepare payload for saving
			const payload = {
				payrollId: payrollId,
				employeeIds: selectedEmployees,
			};

			// Call save API
			const response =
				await payrollApi.setAdvanceEmployeeLeaveAndGratuityPay(payload);

			if (response?.data) {
				toastText('Employees added successfully', 'success');

				// Refresh the main table with new data
				await fetchLeaveGratuityData(payrollId);

				// Close modal and reset state
				closeEmployeeModal();
			}
		} catch (error: any) {
			const errorMessage =
				error?.response?.data?.message || 'Failed to add employees';
			console.error('Error adding employees:', error);
			toastText(errorMessage, 'error');
		} finally {
			setIsSavingEmployees(false);
		}
	};

	const closeEmployeeModal = () => {
		setIsAddEmployeeModal(false);
		setSelectedEmployees([]);
		setSearchText('');
		setBranchIds([]);
		setDepartmentIds([]);
		setModalEmployees([]);
		setModalEmployeesTotal(0);
	};

	const fetchLeaveGratuityData = async (payrollId: string) => {
		try {
			const query = {
				payrollId: payrollId,
			};
			setLeavePayLoading(true);
			setIsUniversalLoading(true);
			let leaveGratuity;
			if (onlyView) {
				leaveGratuity =
					await viewPayrollApi.getAllEmployeesLeaveAndGratuity(query);
			} else {
				leaveGratuity = await payrollApi.getLeaveGratuityPay(query);
			}
			setLeaveGratuityData(leaveGratuity.data.data);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in fetching leave and gratuity record';

			toastText(errorMessage, 'error');
		} finally {
			setLeavePayLoading(false);
			setIsUniversalLoading(false);
		}
	};

	useEffect(() => {
		if (payrollId) {
			fetchLeaveGratuityData(payrollId);
		}
	}, [payrollId]);

	// Updated checkbox handler to support both checkbox types
	const handleCheckboxChange = (id: string, value: boolean, name: string) => {
		const updatedData = LeaveGratuityData.map((item: any) => {
			if (item.employeeId === id) {
				return {
					...item,
					[name]: value,
				};
			}
			return item;
		});
		setLeaveGratuityData(updatedData);
	};

	const handleBulkCheckboxChange = (
		type: 'leave' | 'gratuity',
		value: boolean
	) => {
		const updatedData = LeaveGratuityData.map((item: any) => {
			if (type === 'leave' && !item.alreadyLeavePaid && !onlyView) {
				return { ...item, isLeaveIncluded: value };
			}
			if (type === 'gratuity' && !item.alreadyGratuityPaid && !onlyView) {
				return { ...item, isGratuityIncluded: value };
			}
			return item;
		});
		setLeaveGratuityData(updatedData);
	};

	return (
		<>
			<Row>
				<Col span={24}>
					<GlobalHeader
						headerData={headerData}
						toggleRequired={false}
						isLoading={leavePayLoading}
						fetchAllData={() => fetchLeaveGratuityData(payrollId)}
					/>
				</Col>
			</Row>
			<Row>
				<Row
					align="middle"
					justify="space-between"
					style={{ gap: '16px', marginBottom: '16px', width: '100%' }}
				>
					{LeaveGratuityData?.length > 0 && (
						<Col flex="auto">
							<div
								style={{
									background: '#f4f1ff',
									paddingInline: '14px',
									paddingBlock: '10px',
									borderRadius: '6px',
									color: '#584495',
								}}
							>
								<b>Note :</b> Once the Leave Pay and Gratuity
								Pay for a relieved employee have been fully
								processed and the payroll is approved, that
								employee will be automatically removed from the
								PayGroup.
							</div>
						</Col>
					)}

					{!onlyView && (
						<Col style={{ marginLeft: 'auto' }}>
							<Button
								type="primary"
								onClick={() => setIsAddEmployeeModal(true)}
								disabled={isEmployeeLoading}
							>
								Add Employee
							</Button>
						</Col>
					)}
				</Row>
				<Col span={24}>
					<LeaveGratuityPayTable
						isLoading={leavePayLoading}
						handleCheckboxChange={handleCheckboxChange}
						employeeData={LeaveGratuityData}
						onlyView={onlyView}
						tableChangeHandler={tableChangeHandler}
						showSteps={showSteps}
						handleBulkCheckboxChange={handleBulkCheckboxChange}
						fetchLeaveGratuityData={fetchLeaveGratuityData}
					/>
				</Col>
			</Row>

			{isAddEmployeeModal && (
				<Modal
					open={isAddEmployeeModal}
					onCancel={closeEmployeeModal}
					centered
					closable={true}
					style={{
						minWidth: '1000px',
					}}
					footer={null}
					rootClassName="addGratuityModalRoot"
					title={
						<div>
							<h3 style={{ margin: 0 }}>
								Add Employees for Leave Gratuity / Leave
								Encashment
							</h3>
							<p
								style={{
									marginTop: '10px',
									marginBottom: '25px',
									fontSize: '14px',
									color: '#999',
								}}
							>
								Select employees to process advance gratuity or
								leave encashment payments
							</p>
						</div>
					}
				>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							height: '75vh',
						}}
					>
						<div style={{ flexShrink: 0, marginBottom: '16px' }}>
							<Row gutter={16} style={{ marginBottom: '16px' }}>
								<Col span={12}>
									<Input
										prefix={<SearchOutlined />}
										placeholder="Search by Employee Name or ID..."
										value={searchText}
										onChange={(e) =>
											setSearchText(e.target.value)
										}
										size="large"
									/>
								</Col>
								<Col span={6}>
									<SelectDropdown
										defaultOption={true}
										defaultLabel="Select Branch"
										placeholder="Select Branch"
										mode="multiple"
										options={accessibleBranches}
										value={branchIds}
										onChange={(value) => {
											const newBranchIds =
												value as string[];
											setBranchIds(newBranchIds);

											const validDepartments =
												dropdownOptions?.department
													?.filter((item: any) =>
														newBranchIds.includes(
															item.branchId
														)
													)
													.map(
														(item: any) =>
															item.value
													) || [];

											setDepartmentIds((prev) =>
												prev.filter((id) =>
													validDepartments.includes(
														id
													)
												)
											);
										}}
										size="large"
										required={false}
										helperText="Branch is required"
										label=""
										isError={false}
										loading={isBranchesLoading}
										disabled={isBranchesLoading}
									/>
								</Col>
								<Col span={6}>
									<SelectDropdown
										defaultOption={true}
										defaultLabel="Select Department"
										placeholder="Select Department"
										mode="multiple"
										options={
											branchIds.length > 0
												? dropdownOptions?.department?.filter(
														(item: any) =>
															branchIds.includes(
																item.branchId
															)
												  )
												: []
										}
										value={departmentIds}
										onChange={(value) => {
											setDepartmentIds(value as string[]);
										}}
										size="large"
										required={false}
										helperText="Department is required"
										label=""
										disabled={branchIds.length === 0}
										isError={false}
									/>
								</Col>
							</Row>

							<p
								style={{
									margin: 0,
									color: '#666',
									fontSize: '14px',
								}}
							>
								Showing {filteredEmployees.length} of{' '}
								{modalEmployeesTotal} employees
							</p>
						</div>

						<div
							className="employee-table"
							style={{
								flex: 1,
								minHeight: 0,
							}}
						>
							<Table
								columns={[
									{
										title: (
											<Checkbox
												indeterminate={
													selectedEmployees.length >
														0 &&
													selectedEmployees.length <
														filteredEmployees.length
												}
												checked={
													filteredEmployees.length >
														0 &&
													selectedEmployees.length ===
														filteredEmployees.length
												}
												onChange={handleSelectAll}
											/>
										),
										dataIndex: 'checkbox',
										width: 50,
										className: 'bg-white',
										render: (_, record) => (
											<Checkbox
												checked={selectedEmployees.includes(
													record.id
												)}
												onChange={() =>
													handleSelectEmployee(
														record.id
													)
												}
											/>
										),
									},
									{
										title: 'Employee ID',
										dataIndex: 'employeeId',
										key: 'employeeId',
										className: 'bg-white',
									},
									{
										title: 'Employee Name',
										dataIndex: 'employeeName',
										key: 'employeeName',
										className: 'bg-white',
									},
									{
										title: 'Department',
										dataIndex: 'department',
										key: 'department',
										className: 'bg-white',
									},
									{
										title: 'Branch',
										dataIndex: 'branch',
										key: 'branch',
										className: 'bg-white',
									},
								]}
								dataSource={filteredEmployees.map(
									(emp: any) => ({
										...emp,
										key: emp.id,
									})
								)}
								pagination={{
									pageSize: 50,
									total: filteredEmployees.length,
									size: 'small',
									position: ['bottomRight'],
									showSizeChanger: false,
								}}
								loading={isEmployeeLoading}
								scroll={{ x: 600, y: 'calc(100vh - 500px)' }}
								style={{ marginBottom: 0 }}
							/>
						</div>

						<div
							style={{
								flexShrink: 0,
								borderTop: '1px solid #f0f0f0',
								paddingTop: '16px',
							}}
						>
							<Row justify="end" gutter={12}>
								<Col>
									<Button
										onClick={closeEmployeeModal}
										size="large"
										disabled={isSavingEmployees}
									>
										Cancel
									</Button>
								</Col>
								<Col>
									<Button
										type="primary"
										size="large"
										onClick={handleAddEmployees}
										disabled={
											selectedEmployees.length === 0
										}
										loading={isSavingEmployees}
									>
										Add Selected Employees (
										{selectedEmployees.length})
									</Button>
								</Col>
							</Row>
						</div>
					</div>
				</Modal>
			)}
		</>
	);
};

export default LeavePayCalculation;
